import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import APIUtil from 'util/APIUtil';
import {capitalize, listToReadable} from 'util/NameUtil';

export enum InventoryRegistryDataModAction {
  ARCHIVE = 'ARCHIVE',
  MODIFY = 'MODIFY',
  MOVE = 'MOVE',
  REGISTER = 'REGISTER',
  TRANSFER = 'TRANSFER',
  TRANSFER_PLATE = 'TRANSFER_PLATE',
  UNARCHIVE = 'UNARCHIVE',
  UNREGISTER = 'UNREGISTER',
}

export enum DataModsItemType {
  ENTITY = 'entity',
  ENTITY_SCHEMA = 'entity schema',
  CONTAINER = 'container',
  // use this instead if you need plate well language in your summary
  CONTAINER_OR_PLATE_WELL = 'container or plate well',
  PLATE = 'plate',
  BOX = 'box',
  LOCATION = 'location',
  SOURCE = 'project',
  FOLDER = 'folder and any nested items',
  REGISTRY = 'registry',
}

const SPECIAL_PLURALS: {[term: string]: string} = {
  [DataModsItemType.CONTAINER_OR_PLATE_WELL]: 'containers or plate wells',
  [DataModsItemType.FOLDER]: 'folders and any nested items',
};

export function itemCountsForChangeSummary(
  itemCounts: {[itemType: string]: number},
  {
    modifyContainedSamples = false,
    modifyContainedStorables = false,
  }: {modifyContainedSamples?: boolean; modifyContainedStorables?: boolean} = {}
): string {
  const itemCountLabels = Object.entries(itemCounts)
    .filter(([, count]) => count > 0)
    .map(([itemName, count]) => `${count} ${pluralize(count, itemName, SPECIAL_PLURALS[itemName])}`);
  if (modifyContainedStorables) {
    itemCountLabels.push('any nested inventory items');
  }
  if (modifyContainedSamples) {
    itemCountLabels.push('any contained samples');
  }
  return listToReadable(itemCountLabels);
}

export function getItemCountsFromApiIds(
  apiIds: Array<string>,
  containersMayBeWells: boolean = false
): {[itemType in DataModsItemType]?: number} {
  return {
    [DataModsItemType.ENTITY]: apiIds.filter((apiId) => APIUtil.isBiologicalEntity(apiId)).length,
    [DataModsItemType.ENTITY_SCHEMA]: apiIds.filter((apiId) => APIUtil.isTagSchema(apiId)).length,
    [containersMayBeWells ? DataModsItemType.CONTAINER_OR_PLATE_WELL : DataModsItemType.CONTAINER]:
      apiIds.filter((apiId) => APIUtil.isContainer(apiId)).length,
    [DataModsItemType.BOX]: apiIds.filter((apiId) => APIUtil.isBox(apiId)).length,
    [DataModsItemType.PLATE]: apiIds.filter((apiId) => APIUtil.isPlate(apiId)).length,
    [DataModsItemType.LOCATION]: apiIds.filter((apiId) => APIUtil.isLocation(apiId)).length,
    [DataModsItemType.SOURCE]: apiIds.filter((apiId) => APIUtil.isSource(apiId)).length,
    [DataModsItemType.FOLDER]: apiIds.filter((apiId) => APIUtil.isFolder(apiId)).length,
  };
}

function getActionVerb(action: InventoryRegistryDataModAction): string {
  switch (action) {
    case InventoryRegistryDataModAction.ARCHIVE:
      return 'archived';
    case InventoryRegistryDataModAction.MODIFY:
      return 'modified';
    case InventoryRegistryDataModAction.MOVE:
      return 'moved';
    case InventoryRegistryDataModAction.REGISTER:
      return 'registered';
    case InventoryRegistryDataModAction.TRANSFER:
      return 'modified';
    case InventoryRegistryDataModAction.UNARCHIVE:
      return 'unarchived';
    case InventoryRegistryDataModAction.UNREGISTER:
      return 'unregistered';
    default:
      assert(false, `Unexpected data mod action ${action}`);
  }
}

function getRegisterChangeSummary(
  itemCounts: {[itemType in DataModsItemType]?: number},
  entitiesToMergeCount: number
): JSX.Element {
  const entitiesBeingRegisteredCount = itemCounts[DataModsItemType.ENTITY] ?? 0;
  assert(
    entitiesToMergeCount <= entitiesBeingRegisteredCount,
    'Merge count should not be greater than attempted register count'
  );
  if (entitiesBeingRegisteredCount === 0) {
    return <></>;
  }

  const itemCountsWithoutMergedEntities = {
    ...itemCounts,
    [DataModsItemType.ENTITY]: entitiesBeingRegisteredCount - entitiesToMergeCount,
  };
  const itemsChanged = itemCountsForChangeSummary(itemCountsWithoutMergedEntities, {
    modifyContainedSamples: false,
    modifyContainedStorables: false,
  });
  const successfulRegisterSummary = itemsChanged ? (
    <p>{`${itemsChanged} will be ${getActionVerb(InventoryRegistryDataModAction.REGISTER)}.`}</p>
  ) : (
    <></>
  );

  return entitiesToMergeCount > 0 ? (
    <>
      {successfulRegisterSummary}
      {getRegisterMergeOnlyChangeSummary(entitiesToMergeCount)}
    </>
  ) : (
    successfulRegisterSummary
  );
}

/**
 * Returns change summary for inventory/registry modifications.
 *
 * @param apiIds: The api identifiers that will be modified
 * @param action: The data mod action (MODIFY for generic)
 * @param itemCounts: Counts specified by item type (to be used when counts available, but ids are not)
 * @param modifyContainedSamples: True if contained samples not in apiIds will be modified (e.g. for archive)
 * @param modifyContainedStorables: True if nested storables not in apiIds will be modified (e.g. for archive)
 * @param spreadsheetImportConfig: Config for generic spreadsheet message; required for SPREADSHEET action
 * @param entitiesToMergeCount: How many entities will be merged as a result of this REGISTER action. These
 *        entities are also represented in apiIds / itemCounts
 * @param containersMayBeWells: True if modified containers may be well plate wells
 */
export function getInventoryRegistryChangeSummary({
  apiIds,
  action,
  itemCounts,
  modifyContainedSamples = false,
  modifyContainedStorables = false,
  entitiesToMergeCount = 0,
  containersMayBeWells = false,
}: {
  apiIds?: Array<string>;
  action: InventoryRegistryDataModAction;
  itemCounts?: {[itemType in DataModsItemType]?: number};
  modifyContainedSamples?: boolean;
  modifyContainedStorables?: boolean;
  entitiesToMergeCount?: number;
  containersMayBeWells?: boolean;
}): string | JSX.Element {
  const derivedItemCounts = itemCounts ?? getItemCountsFromApiIds(apiIds ?? [], containersMayBeWells);
  const itemsChanged = itemCountsForChangeSummary(derivedItemCounts, {
    modifyContainedSamples,
    modifyContainedStorables,
  });

  assert(itemsChanged !== '', 'Expected api identifiers for change summary calculation');
  switch (action) {
    case InventoryRegistryDataModAction.REGISTER:
      return getRegisterChangeSummary(derivedItemCounts, entitiesToMergeCount);
    case InventoryRegistryDataModAction.TRANSFER_PLATE:
      return `Containers or wells in ${itemsChanged || 'plates'} will be modified.`;
    default:
      return `${itemsChanged || 'Items'} will be ${getActionVerb(action)}.`;
  }
}

/**
 * Returns change summary for inventory/registry modifications done via spreadsheet upload.
 *
 * This function is separate from `getInventoryRegistryChangeSummary` because API IDs and other info are
 * not available in the context of spreadsheet uploads (for now).
 *
 * @param itemTypes: Types of items being modified
 */
export function getInventoryRegistrySpreadsheetChangeSummary(itemTypes: Array<DataModsItemType>): string {
  assert(itemTypes.length > 0, 'Must provide list of item types');
  return capitalize(
    `${listToReadable(
      itemTypes.map((itemType) => pluralize(2, itemType, SPECIAL_PLURALS[itemType]))
    )} will be modified according to this spreadsheet.`
  );
}

/**
 * Returns change summary for registry merge flows. Only call this / use datamods when you know that a registry
 * merge will happen and you only want to show a summary of the merge part.
 */
export function getRegisterMergeOnlyChangeSummary(duplicateCount: number): JSX.Element {
  if (duplicateCount < 1) {
    return <></>;
  }
  const message =
    duplicateCount === 1
      ? '1 entity being registered is a duplicate of some entity in the registry and ' +
        'will be merged into the existing entity. '
      : `${duplicateCount} entities being registered are duplicates of some entities in the registry and ` +
        'will be merged into the existing entities. ';
  return (
    <p>
      {message}
      <a
        rel="noopener noreferrer"
        target="_blank"
        href="https://help.benchling.com/hc/articles/9684256435597"
      >
        Learn more
      </a>
    </p>
  );
}
